import {inject, Injectable} from '@angular/core';
import {StateManagerService} from '@core/services';
import {AppSettings, AppState, StateManager} from '@core/types';
import {CONFIG, log, STORAGE_KEYS} from '@core/utils';

@Injectable({
  providedIn: 'root'
})
export class AppStateService {

  private stateManager = inject(StateManagerService);

  private state: StateManager<AppState> = this.stateManager.createState<AppState>({
    storage: 'localStorage',
    key: STORAGE_KEYS.APP,
    storageOptions: {
      ttl: CONFIG.TTL_APP,
      prefix: 'app',
      encrypt: false
    },
    initialValue: {
      settings: {
        theme: 'light',
        language: 'es',
        sidebarCollapsed: false,
        notifications: true,
      },
      isOnline: navigator.onLine,
      isDev: CONFIG.isDebug(),
      sidebarStatus: 'close'
    },
  });

  theme = this.state.computed(state => state.settings.theme);
  isOnline = this.state.computed(state => state.isOnline);
  isDev = this.state.computed(state => state.isDev);
  settings = this.state.computed(state => state.settings);
  sidebarStatus = this.state.computed(state => state.sidebarStatus);

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.setOnlineStatus(true));
      window.addEventListener('offline', () => this.setOnlineStatus(false));
    }
  }

  async initialize() {
    await this.state.loadFromStorage();
    log.debug('App state initialized');
  }

  updateSettings = (settingsUpdate: Partial<AppSettings>) => this.state.update(current => ({
    ...current,
    settings: {...current.settings, ...settingsUpdate}
  }));

  toggleTheme = () => this.state.update(current => ({
    ...current,
    settings: {
      ...current.settings,
      theme: current.settings.theme === 'light' ? 'dark' : 'light'
    }
  }));

  toggleSidebar = () => this.state.update(current => {
    const isWide = typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;

    // Desktop: alterna entre mini y open (close se considera mini)
    if (isWide) {
      const isOpen = current.sidebarStatus === 'open';
      return {
        ...current,
        sidebarStatus: isOpen ? 'mini' : 'open'
      };
    }

    // Mobile: alterna entre close y open (mini se considera close)
    const isOpen = current.sidebarStatus === 'open';
    return {
      ...current,
      sidebarStatus: isOpen ? 'close' : 'open'
    };
  });

  setLanguage(language: 'es' | 'en'): void {
    this.updateSettings({language});
  }

  private setOnlineStatus = (online: boolean) => this.state.update(current => ({
    ...current,
    isOnline: online
  }));
}
